import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ConnectedServiceType, ILocationsStatistic, ILookupValue, IVoicePlatformAccessStatistic } from '../../model';
import { getApi, isApiCancel } from './api';
import { GridDataResult } from '../../model/api/nexParkInstance/lotDirectory';
import { IFeaturesOverview } from '../../model/api/nexParkInstance/operations';

const URL = '/nexParkInstance';

const getLookupValues = async (): Promise<ILookupValue[]> => {
  const api = await getApi();
  return api.get<ILookupValue[]>(`${URL}/lookup-values`).then((response) => response.data || []);
};

const getLookupValuesByCustomer = async (customerId: number): Promise<ILookupValue[]> => {
  const api = await getApi();
  return api.get<ILookupValue[]>(`${URL}/by-customer?id=${customerId}`).then((response) => response.data || []);
};

export const getLocationsList = async (customerId: number, state: State): Promise<GridDataResult> => {
  const params = {
    group: state.group,
    filter: state.filter,
    skip: state.skip,
    limit: state.take,
    sort: state.sort,
  };

  try {
    const api = await getApi();
    const response = await api.post<GridDataResult>(`${URL}/${customerId}/lots`, params);
    return response.data || [];
  } catch (error) {
    console.error('Error fetching locations list:', error);
    throw error;
  }
};

export const getNexParkInstanceUrl = async (customerId: number): Promise<string> => {
  try {
    const api = await getApi();
    const response = await api.get(`${URL}/${customerId}/instance-url`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching instance URL for customer ${customerId}:`, error);
    throw error;
  }
};

export interface BillableStatus {
  lotId: number;
  isBillable: boolean;
}

export const setLotBillableStatus = async (customerId: number, request: BillableStatus) => {
  try {
    const api = await getApi();
    await api.put(`${URL}/${customerId}/lots/billable-status`, request);
  } catch (error) {
    console.error(`Error setting billable status for lot ${customerId}:`, error);
    throw error;
  }
};

export interface BulkSetLotBillableStatusRequest {
  lotIds: number[];
  isBillable: boolean;
}

export const bulkSetLotBillableStatus = async (customerId: number, request: BulkSetLotBillableStatusRequest) => {
  try {
    const api = await getApi();
    await api.put(`${URL}/${customerId}/lots/bulk/billable-status`, request);
  } catch (error) {
    console.error(`Error during bulk update of billable statuses:`, error);
    throw error;
  }
};

export const getLocationsStatistic = async (customerId: number, signal?: AbortSignal): Promise<ILocationsStatistic> => {
  try {
    const api = await getApi();
    const response = await api.get<ILocationsStatistic>(`${URL}/${customerId}/lots/statistic`, { signal });
    return response.data;
  } catch (error) {
    if (!isApiCancel(error)) {
      console.error(`Error fetching locations statistic for customer ${customerId}:`, error);
    }
    throw error;
  }
};

export const getVoicePlatformAccessStatistic = async (customerId: number, signal?: AbortSignal): Promise<IVoicePlatformAccessStatistic> => {
  try {
    const api = await getApi();
    const response = await api.get<IVoicePlatformAccessStatistic>(`${URL}/${customerId}/lots/voice-platform-access-statistic`, { signal });
    return response.data;
  } catch (error) {
    if (!isApiCancel(error)) {
      console.error(`Error fetching voice platform access statistic for customer ${customerId}:`, error);
    }
    throw error;
  }
};

export const getFeaturesOverview = async (dataState: State): Promise<IFeaturesOverview> => {
  try {
    const querySegments: Array<string> = [];

    querySegments.push(`connectedServiceId=${ConnectedServiceType.NexPark}`);

    const kendoQueryStr = toDataSourceRequestString(dataState);
    if (kendoQueryStr) {
      querySegments.push(kendoQueryStr);
    }

    const api = await getApi();
    const response = await api.get<IFeaturesOverview>(`NexParkOperations/features/overview?${querySegments.join('&')}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching NexPark features overview:`, error);
    throw error;
  }
};

export const nexParkInstanceService = {
  getLookupValues,
  getLookupValuesByCustomer,
  getLocationsList,
  getNexParkInstanceUrl,
  setLotBillableStatus,
  bulkSetLotBillableStatus,
  getFeaturesOverview,
};
